import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { authService } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import { consumeRedirectAfterLogin } from "@/utils/functions";
import { useAppConfig } from "@/hooks/useAppConfig.js";
import { postLoginPath } from "@/utils/identityGate";
import {
  loadGoogleSDK,
  triggerGoogleSignIn,
  loadAppleSDK,
  triggerAppleSignIn,
  getSocialAuthConfig,
  decodeJwt,
} from "@/utils/socialAuth";

/**
 * `onProfile` switches the buttons from SIGNING IN to FILLING IN: the provider is asked who the
 * person is, and {firstName, lastName, email} is handed back — no session is created or
 * replaced. For a signed-in passenger completing their profile, where logging in as the Google
 * account would swap them into a different user. The values are only a pre-fill; the form
 * still verifies the email.
 */
export default function SocialLoginButtons({ disabled, onProfile, onBusyChange, large = false }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [error, setError] = useState("");

  // The config is passed INTO getSocialAuthConfig rather than read from the cache inside
  // it — see the note there. This also makes the subscription explicit: when
  // /config/app lands, `appConfig` changes and this component re-renders, which is what
  // flips the button from disabled to active.
  const { data: appConfig, isError: configFailed } = useAppConfig();
  const { googleClientId, appleServiceId, appleRedirectURI } = getSocialAuthConfig(appConfig);
  const googleReady = !!googleClientId;
  const appleReady = !!appleServiceId;

  const isLoading = googleLoading || appleLoading;
  // `large` = the bigger 56px buttons (the profile form on the confirm pages); the sign-in and
  // sign-up screens keep the 48px ones.
  const sizeClass = large ? "w-14 h-14 [&_svg]:scale-125" : "w-12 h-12";
  // Lets a form around the buttons lock its own fields while a provider popup is open.
  useEffect(() => { onBusyChange?.(isLoading); }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleLogin = async () => {
    if (isLoading || disabled) return;
    // Unreachable while the button is disabled without an id — a guard so a future
    // caller can't hand the SDK an empty string.
    if (!googleReady) return;
    setGoogleLoading(true);
    setError("");

    try {
      await loadGoogleSDK();
      const idToken = await triggerGoogleSignIn(googleClientId);
      if (onProfile) {
        const claims = decodeJwt(idToken);
        onProfile({ firstName: claims.given_name || "", lastName: claims.family_name || "", email: claims.email || "" });
        return;
      }
      const result = await authService.loginWithGoogle(idToken, "passenger");

      if (result.success && result.data?.token) {
        login(result.data.token);
        // Google gives a verified email and a name, never a phone — the gate's first cause.
        navigate(postLoginPath(result.data.user, consumeRedirectAfterLogin()), { replace: true });
      } else {
        setError(result.message || "Google login failed");
      }
    } catch (err) {
      if (err.message === "popup_blocked") {
        setError("Popups are blocked. Please allow popups and try again.");
      } else if (err.message !== "Sign-in cancelled") {
        setError(err.message || "Google login failed");
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    if (isLoading || disabled) return;
    // Same guard as Google: unreachable while the button is disabled without a
    // Services ID, but it keeps a future caller from handing the SDK an empty string.
    if (!appleReady) return;
    setAppleLoading(true);
    setError("");

    try {
      await loadAppleSDK();
      const { id_token, firstName, lastName } = await triggerAppleSignIn({
        clientId: appleServiceId,
        redirectURI: appleRedirectURI,
      });
      if (onProfile) {
        // Apple's relay addresses forward mail but aren't something a driver or a receipt
        // should be sent to — leave the email for the person to type.
        const email = decodeJwt(id_token).email || "";
        onProfile({ firstName, lastName, email: /privaterelay\.appleid\.com$/i.test(email) ? "" : email });
        return;
      }
      // firstName/lastName are empty on every sign-in but the user's first —
      // see the note in triggerAppleSignIn. Forwarded so the account isn't
      // created nameless.
      const result = await authService.loginWithApple(id_token, "passenger", { firstName, lastName });

      if (result.success && result.data?.token) {
        login(result.data.token);
        // Apple gives an email (usually a private relay) and never a phone.
        navigate(postLoginPath(result.data.user, consumeRedirectAfterLogin()), { replace: true });
      } else {
        setError(result.message || "Apple login failed");
      }
    } catch (err) {
      if (err.message !== "Sign-in cancelled") {
        setError(err.message || "Apple login failed");
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const appleIcon = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.4951 10.505C14.4851 9.19499 15.0601 8.19999 16.2251 7.45999C15.5701 6.52499 14.5601 6.00999 13.2151 5.90499C11.9401 5.80499 10.5401 6.63499 10.0401 6.63499C9.51005 6.63499 8.28005 5.94499 7.30005 5.94499C5.27005 5.97499 3.11005 7.48999 3.11005 10.67C3.11005 11.625 3.29005 12.61 3.65005 13.625C4.13005 14.955 5.86005 18.015 7.66005 17.955C8.56005 17.93 9.20005 17.335 10.3801 17.335C11.5201 17.335 12.1151 17.955 13.1201 17.955C14.9351 17.925 16.4901 15.125 16.9451 13.79C14.3701 12.56 14.4951 10.58 14.4951 10.505ZM12.3451 4.56999C13.3001 3.43499 13.2101 2.39999 13.1801 2.04499C12.3401 2.09499 11.3701 2.61999 10.8201 3.25499C10.2201 3.93499 9.86505 4.77499 9.94005 5.89499C10.8451 5.96499 11.6701 5.48999 12.3451 4.56999Z" fill="black" />
    </svg>
  );

  return (
    <>
      {error && (
        <p className="text-sm text-red-500 text-center mb-3">{error}</p>
      )}
      <div className="flex items-center justify-center gap-4">
        {/* Google */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          // Not clickable until the client id is in hand — it arrives with /config/app,
          // so the button is briefly disabled on a cold load. If it NEVER enables, that
          // request is failing, and the title says which.
          disabled={isLoading || disabled || !googleReady}
          title={
            googleReady
              ? "Continue with Google"
              : configFailed
                ? "Google sign-in unavailable — the app config didn't load"
                : "Loading Google sign-in…"
          }
          className={`flex items-center justify-center ${sizeClass} rounded-full border border-gray-200 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {googleLoading ? (
            <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z" fill="#4285F4" />
              <path d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z" fill="#34A853" />
              <path d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z" fill="#FBBC05" />
              <path d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z" fill="#EB4335" />
            </svg>
          )}
        </button>
        {/* Apple. Same contract as Google above: not clickable until the Services ID
            arrives with /config/app, and the title says which state it is in. */}
        <button
          type="button"
          onClick={handleAppleLogin}
          disabled={isLoading || disabled || !appleReady}
          title={
            appleReady
              ? "Continue with Apple"
              : configFailed
                ? "Apple sign-in unavailable — the app config didn't load"
                : "Loading Apple sign-in…"
          }
          className={`flex items-center justify-center ${sizeClass} rounded-full border border-gray-200 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {appleLoading ? (
            <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
          ) : appleIcon}
        </button>
      </div>
    </>
  );
}
